import { schema } from 'normalizr';
import { createActions, createReducer } from 'reduxsauce';

import {
  entityFetching,
  entityFetchingFailure,
  entityFetchingSuccess,
} from '../../utils/redux';

export const provincesEntity = new schema.Entity('provinces');
export const citiesEntity = new schema.Entity('cities');

export const INITIAL_STATE = {
  entities: {
    [provincesEntity.key]: {},
    [citiesEntity.key]: {},
  },
  result: {
    [provincesEntity.key]: {
      fetching: false,
      fetched: false,
      results: [],
      error: null,
    },
    [citiesEntity.key]: {
      fetching: false,
      fetched: false,
      results: [],
      error: null,
    },
  },
  // Not a list/entity like provinces/cities above -- a single resolved
  // { id, name, province } result (or null) for the last /deals/:citySlug
  // slug looked up, used by DealsByCityRedirect to hand off to
  // CouponsListAll via preSelectedCities. Doesn't use the normalizr
  // entityFetching* helpers since there's nothing to normalize.
  citySlugResolution: {
    fetching: false,
    fetched: false,
    data: null,
    error: null,
  },
  // Same shape as citySlugResolution -- a single resolved
  // { id, name, province, distance_km } result (or null) from resolving
  // the visitor's IP to their nearest registered city. Used by GuestHome
  // to set a default city on first visit when nothing's in localStorage.
  nearestCity: {
    fetching: false,
    fetched: false,
    data: null,
    error: null,
  },
};

const { Types, Creators } = createActions(
  {
    getProvinces: [],
    getProvincesRequest: [],
    getProvincesSuccess: ['data'],
    getProvincesFailure: ['error'],

    getProvincesNextPage: [],
    getProvincesNextPageRequest: [],
    getProvincesNextPageSuccess: ['data'],
    getProvincesNextPageFailure: ['error'],

    getCities: ['provinceName', 'name'],
    getCitiesRequest: [],
    getCitiesSuccess: ['data'],
    getCitiesFailure: ['error'],

    // Same as getCities but with bebounced watcher
    getFilteredCities: ['provinceName', 'name'],

    getCitiesNextPage: [],
    getCitiesNextPageRequest: [],
    getCitiesNextPageSuccess: ['data'],
    getCitiesNextPageFailure: ['error'],

    // Resolves a /deals/:citySlug slug (e.g. "windsor-on") to a real
    // { id, name, province } via the backend -- same slug-parsing logic as
    // coupons/bot_views.py's deals_by_city_ssr, just returned as JSON.
    resolveCitySlug: ['citySlug'],
    resolveCitySlugRequest: [],
    resolveCitySlugSuccess: ['data'],
    resolveCitySlugFailure: ['error'],

    // Resolves the visitor's IP to their nearest registered city, for
    // setting a default city on first visit (no stored city yet).
    getNearestCity: [],
    getNearestCityRequest: [],
    getNearestCitySuccess: ['data'],
    getNearestCityFailure: ['error'],
  },
  { prefix: 'cb/locations/' },
);

export { Types as types, Creators as actions };

const resolveCitySlugRequest = state => ({
  ...state,
  citySlugResolution: {
    ...state.citySlugResolution,
    fetching: true,
    fetched: false,
    error: null,
  },
});

const resolveCitySlugSuccess = (state, { data }) => ({
  ...state,
  citySlugResolution: {
    fetching: false,
    fetched: true,
    data,
    error: null,
  },
});

const resolveCitySlugFailure = (state, { error }) => ({
  ...state,
  citySlugResolution: {
    fetching: false,
    fetched: true,
    data: null,
    error,
  },
});

const getNearestCityRequest = state => ({
  ...state,
  nearestCity: {
    ...state.nearestCity,
    fetching: true,
    fetched: false,
    error: null,
  },
});

const getNearestCitySuccess = (state, { data }) => ({
  ...state,
  nearestCity: {
    fetching: false,
    fetched: true,
    data,
    error: null,
  },
});

const getNearestCityFailure = (state, { error }) => ({
  ...state,
  nearestCity: {
    fetching: false,
    fetched: true,
    data: null,
    error,
  },
});

export const HANDLERS = {
  [Types.GET_PROVINCES_REQUEST]: entityFetching(provincesEntity),
  [Types.GET_PROVINCES_SUCCESS]: entityFetchingSuccess(provincesEntity),
  [Types.GET_PROVINCES_FAILURE]: entityFetchingFailure(provincesEntity),
  [Types.GET_PROVINCES_NEXT_PAGE_REQUEST]: entityFetching(provincesEntity),
  [Types.GET_PROVINCES_NEXT_PAGE_SUCCESS]: entityFetchingSuccess(
    provincesEntity,
    { attach: true },
  ),
  [Types.GET_PROVINCES_NEXT_PAGE_FAILURE]: entityFetchingFailure(
    provincesEntity,
  ),
  [Types.GET_CITIES_REQUEST]: entityFetching(citiesEntity),
  [Types.GET_CITIES_SUCCESS]: entityFetchingSuccess(citiesEntity),
  [Types.GET_CITIES_FAILURE]: entityFetchingFailure(citiesEntity),
  [Types.GET_CITIES_NEXT_PAGE_REQUEST]: entityFetching(citiesEntity),
  [Types.GET_CITIES_NEXT_PAGE_SUCCESS]: entityFetchingSuccess(citiesEntity, {
    attach: true,
  }),
  [Types.GET_CITIES_NEXT_PAGE_FAILURE]: entityFetchingFailure(citiesEntity),

  [Types.RESOLVE_CITY_SLUG_REQUEST]: resolveCitySlugRequest,
  [Types.RESOLVE_CITY_SLUG_SUCCESS]: resolveCitySlugSuccess,
  [Types.RESOLVE_CITY_SLUG_FAILURE]: resolveCitySlugFailure,

  [Types.GET_NEAREST_CITY_REQUEST]: getNearestCityRequest,
  [Types.GET_NEAREST_CITY_SUCCESS]: getNearestCitySuccess,
  [Types.GET_NEAREST_CITY_FAILURE]: getNearestCityFailure,
};

export default createReducer(INITIAL_STATE, HANDLERS);